import { useEffect, useRef, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TimelineCliente } from '@/components/TimelineCliente';

export const ClienteBuscador = ({ label, value, onChangeText, onSelect, placeholder, testid, autoFocus, telefonoActual = '' }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [crear, setCrear] = useState(false);
  const [nuevo, setNuevo] = useState({ telefono: '', sa: false, cyh: false });
  const [creando, setCreando] = useState(false);
  const timer = useRef(null);
  const skipNext = useRef(false);
  const crearRef = useRef(false);
  crearRef.current = crear;

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = (value || '').trim();
    setCrear(false);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await api.get(`/clientes?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data.clientes || []);
        setOpen(true);
      } catch { /* sin resultados */ }
      setBuscando(false);
    }, 300);
    return () => clearTimeout(timer.current);
  }, [value]);

  const pick = (c) => {
    skipNext.current = true;
    setOpen(false);
    setCrear(false);
    setResults([]);
    onSelect(c);
  };

  const abrirCrear = () => {
    setNuevo({ telefono: telefonoActual || '', sa: false, cyh: false });
    setCrear(true);
  };

  const crearFicha = async () => {
    setCreando(true);
    try {
      const { data } = await api.post('/clientes', {
        nombre: (value || '').trim(), telefono: nuevo.telefono.trim(),
        empresa_sa: nuevo.sa, empresa_cyh: nuevo.cyh, origen: 'vendedor',
      });
      if (data.duplicado) {
        toast.info(`Ya existía la ficha ${data.duplicado.id} — se seleccionó ese cliente`);
        pick(data.duplicado);
      } else {
        toast.success(`Ficha ${data.id} creada ✅`);
        pick(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error creando cliente');
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="relative">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChangeText(e.target.value)} placeholder={placeholder}
        className="bg-[var(--bg)] border-[var(--border)]" autoFocus={autoFocus} autoComplete="off"
        onBlur={() => setTimeout(() => { if (!crearRef.current) setOpen(false); }, 150)}
        onFocus={() => (results.length > 0 || (value || '').trim().length >= 2) && setOpen(true)}
        data-testid={testid} />
      {buscando && <p className="text-[10px] text-[var(--text-faint)] mt-0.5">Buscando clientes…</p>}
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-[var(--surface)] border border-[#6C3FB5]/40 rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testid}-resultados`}>
          {results.map((c) => (
            <button key={c.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              className="w-full text-left px-3 py-2 hover:bg-[#6C3FB5]/10 border-b border-[var(--border)] last:border-0"
              data-testid={`${testid}-opcion-${c.id}`}>
              <p className="text-xs font-bold text-[var(--text)]">
                {c.nombre} <span className="text-[9px] font-black text-[#6C3FB5]">{c.id}</span>
              </p>
              <p className="text-[10px] text-[var(--text-muted)]">
                {[c.telefono || c.whatsapp, c.vendedor_nombre].filter(Boolean).join(' · ') || 'Sin teléfono'}
              </p>
            </button>
          ))}
          {!crear ? (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); abrirCrear(); }}
              className="w-full text-left px-3 py-2 bg-[#6C3FB5]/5 hover:bg-[#6C3FB5]/15 border-t border-[#6C3FB5]/20"
              data-testid={`${testid}-crear-btn`}>
              <p className="text-xs font-black text-[#6C3FB5]">➕ Crear ficha de cliente "{(value || '').trim()}"</p>
              <p className="text-[9px] text-[var(--text-muted)]">Se creará una ficha CL-XXXX asignada a ti</p>
            </button>
          ) : (
            <div className="px-3 py-2.5 bg-[#6C3FB5]/5 border-t border-[#6C3FB5]/20 space-y-2" data-testid={`${testid}-crear-form`}>
              <p className="text-[10px] font-black uppercase text-[#6C3FB5]">Nueva ficha: {(value || '').trim()}</p>
              <Input value={nuevo.telefono} onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value })}
                placeholder="Teléfono (opcional)" className="h-8 text-xs bg-[var(--bg)] border-[var(--border)]"
                data-testid={`${testid}-crear-telefono`} />
              <div className="flex gap-1.5">
                {[{ k: 'cyh', l: 'CYH' }, { k: 'sa', l: 'SA' }].map((e) => (
                  <button key={e.k} type="button" onClick={() => setNuevo({ ...nuevo, [e.k]: !nuevo[e.k] })}
                    className={`flex-1 text-[10px] font-bold px-2 py-1.5 rounded border transition-colors ${nuevo[e.k] ? 'bg-[#6C3FB5] text-white border-[#6C3FB5]' : 'bg-[var(--bg)] text-[var(--text-muted)] border-[var(--border)]'}`}
                    data-testid={`${testid}-crear-${e.k}`}>{e.l}</button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => setCrear(false)}
                  className="flex-1 text-[10px] font-bold px-2 py-1.5 rounded border border-[var(--border)] text-[var(--text-muted)]"
                  data-testid={`${testid}-crear-cancelar`}>Cancelar</button>
                <button type="button" onClick={crearFicha} disabled={creando}
                  className="flex-1 text-[10px] font-black px-2 py-1.5 rounded bg-[#6C3FB5] text-white disabled:opacity-50"
                  data-testid={`${testid}-crear-guardar`}>{creando ? 'Creando…' : 'Crear y usar'}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EMO = { call: '📞', visit: '📍', quote: '📄', sale: '💰' };

export const HistorialCliente = ({ nombre, telefono, clienteId }) => {
  const [data, setData] = useState(null);
  const [verTimeline, setVerTimeline] = useState(false);
  useEffect(() => {
    if (!nombre) { setData(null); return; }
    api.get(`/work-plan/activities/cliente?nombre=${encodeURIComponent(nombre)}&telefono=${encodeURIComponent(telefono || '')}`)
      .then((r) => setData(r.data.actividades || []))
      .catch(() => setData([]));
  }, [nombre, telefono]);
  if (!nombre || data === null) return null;
  return (
    <div className="mt-2 bg-[#6C3FB5]/5 border border-[#6C3FB5]/25 rounded-md p-2.5" data-testid="historial-cliente">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[10px] font-black uppercase text-[#6C3FB5]">📋 Historial reciente · {nombre}</p>
        {clienteId && (
          <button type="button" onClick={() => setVerTimeline(true)}
            className="text-[9px] font-black text-[#6C3FB5] underline shrink-0"
            data-testid="historial-ver-timeline">🕒 Línea de tiempo</button>
        )}
      </div>
      {data.length === 0 ? (
        <p className="text-[10px] text-[var(--text-muted)] italic">Sin llamadas ni visitas previas con este cliente.</p>
      ) : data.map((a, i) => (
        <div key={i} className="flex items-start gap-1.5 py-1 border-b border-[var(--border)] last:border-0" data-testid={`historial-item-${i}`}>
          <span className="text-xs shrink-0">{EMO[a.type] || '📌'}</span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-[var(--text)] truncate">
              <b>{a.outcome || (a.type === 'sale' ? 'Venta' : a.type === 'quote' ? 'Cotización' : '—')}</b>
              {a.amount ? ` · Q${Number(a.amount).toLocaleString('es-GT')}` : ''}
              {a.notes ? ` · ${a.notes}` : ''}
            </p>
            <p className="text-[9px] text-[var(--text-muted)]">
              {new Date(a.happened_at).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })}{a.user_name ? ` · ${a.user_name}` : ''}
            </p>
          </div>
        </div>
      ))}
      {verTimeline && clienteId && (
        <Dialog open onOpenChange={(o) => !o && setVerTimeline(false)}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="historial-timeline-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm">🕒 Línea de tiempo · {nombre} <span className="text-[#6C3FB5] text-xs font-black">{clienteId}</span></DialogTitle>
            </DialogHeader>
            <TimelineCliente cid={clienteId} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
